// @ts-strict-ignore
import pickBy from 'lodash/pickBy';

import type {EntryRowTypeHelpers} from 'common/query-builder/query-entry-section/row-types/types';
import {
  Action,
  EMPTY_EVENT_ENTRY,
  EntryPart,
  FiltersOperator,
  FormulaItem,
  isMetricEntry,
  isQuerySuperEntry,
  MetricEntry,
  QueryFormulaEntry,
  QueryMultiMetricEntry,
  QuerySavedMetricEntry,
  QuerySuperEntry,
  SelectorTypeDefinitions,
} from 'common/query-builder/query-entry-section/types';
import {ProjectFeatureFlag, PropertyType, ResourceType} from 'common/report/constants';
import {MetricType, PRIMITIVE_MATH_TYPES} from 'common/types/reports/bookmark';
import {
  isCohortMenuSectionItem,
  isEventMenuSectionItem,
  isFormulaMenuSectionItem,
  isFunnelMenuSectionItem,
  isProfileMenuSectionItem,
  isRetentionMenuSectionItem,
  isSavedMetricMenuSectionItem,
  MenuType,
} from 'common/query-builder/query-block/multi-metrics-menu/types';
import {filterToBookmarkableEventItem} from 'common/query-builder/query-block/events-menu/util';
import {EventItem} from 'common/query-builder/query-block/events-menu/types';
import {isFirstTimeEventFilter, PropertyFilterFactory} from 'common/widgets/property-filter-menu/models/utils';
import {getNewEntry as getNewFunnelEntry} from 'common/query-builder/query-entry-section/row-types/metric-entry/metrics/funnels/utils';
import {getNewEntry as getNewRetentionEntry} from 'common/query-builder/query-entry-section/row-types/metric-entry/metrics/retention/utils';
import {getNewInlineEntry as getNewFormulaEntry} from 'common/query-builder/query-entry-section/row-types/formula-entry/utils';
import {BehaviorShowClause, MetricBehavior, MetricMeasurement} from 'common/report/insights/util/types';
import {PropertySectionItem} from 'common/query-builder/query-block/properties-menu/item-menu-types';
import {filterToBookmarkablePropertyItem} from 'common/query-builder/query-block/properties-menu/util';
import {isScdProperty} from 'common/query-builder/query-block/properties-menu/types';
import {DisplayContext} from 'common/widgets/property-filter-menu/models/types';
import {TopLevelMetricType} from 'common/types/reports/bookmark';

import {getMetricEntryBehavior} from '../metric-entry/metrics/metric-entry-util';
// eslint-disable-next-line import/no-cycle
import {MultiMetricsEntryRow} from './index';
import {getNewMetricEntry} from './metric-helper';

export const helpers: EntryRowTypeHelpers<QueryMultiMetricEntry> = {
  component: MultiMetricsEntryRow,
  getNewEntry: () => {
    return {
      type: SelectorTypeDefinitions.Metrics,
      event: null,
      filters: [],
    };
  },
  isPlaceholder: () => {
    return true;
  },

  /** @todo(metrics) marked for deletion */
  handleRowSelect: (props, sectionItem, prevEntry) => {
    let dataGroupId = props.sectionProps.dataGroupId || null;
    if (props.sectionProps.metricLevelDataGroups && !prevEntry) {
      // new entries get the same dataGroupId as the previous entry so we don't remove filters/breakdowns unnecessarily
      // (mismatched dataGroupIds can cause filters/breakdowns to be removed, see `sanitizeSelectionForDataGroups`)
      dataGroupId = props.sectionProps.entries?.at(-1)?.dataGroupId || null;
    }

    if (!sectionItem.menuType || isEventMenuSectionItem(sectionItem)) {
      const itemValue = sectionItem.value;
      const event = filterToBookmarkableEventItem(itemValue as EventItem);
      let entry: QuerySuperEntry | null = null;
      if (prevEntry && isQuerySuperEntry(prevEntry) && prevEntry.type === ResourceType.Event) {
        entry = {...prevEntry, event};
      } else {
        entry = {
          ...EMPTY_EVENT_ENTRY,
          ...(dataGroupId ? {dataGroupId} : null),
          aggregationOperator: PRIMITIVE_MATH_TYPES.UNIQUE,
          event,
        };
      }
      const rowFilteringCapabilities = props.getFilteringCapabilities(entry);
      if (!rowFilteringCapabilities.canApplyFirstTimeEventFilter) {
        entry.filters = entry.filters.filter((filter) => !isFirstTimeEventFilter(filter));
      }
      props.updateEntry(Action.Edit, entry);
    } else if (isProfileMenuSectionItem(sectionItem)) {
      const itemValue = sectionItem.value;
      const event = {
        custom: false,
        label: `Analyze Profiles`,
        value: `$all_people`,
      };
      const entry: QuerySuperEntry = {
        ...EMPTY_EVENT_ENTRY,
        ...(dataGroupId ? {dataGroupId} : null),
        event,
        type: ResourceType.User,
      };
      if (itemValue !== `$all_people` && isScdProperty(itemValue)) {
        if (itemValue.type === PropertyType.Number) {
          entry.property = {...itemValue, label: itemValue.label};
        } else {
          const propertyFilter = PropertyFilterFactory.createPropertyFilter(
            {
              ...(itemValue.customProperty ? {customProperty: itemValue.customProperty} : {}),
              ...(itemValue.customPropertyId ? {customPropertyId: itemValue.customPropertyId} : {}),
              ...(itemValue.dataGroupId ? {dataGroupId: itemValue.dataGroupId} : {}),
              ...(itemValue.tempDataGroupId ? {tempDataGroupId: itemValue.tempDataGroupId} : {}),
              resourceType: itemValue.resourceType,
              propertyDefaultType: itemValue.propertyDefaultType || itemValue.type,
              propertyName: itemValue.value,
              ...(itemValue.behavior ? {behavior: itemValue.behavior} : {}),
              propertyType: itemValue.type,
              ...(itemValue.joinPropertyType ? {joinPropertyType: itemValue.joinPropertyType} : {}),
              ...(itemValue.propertyObjectKey ? {propertyObjectKey: itemValue.propertyObjectKey} : {}),
              ...(itemValue.scdEvent ? {scdEvent: itemValue.scdEvent} : {}),
            },
            {displayContext: DisplayContext.Default},
          );
          entry.filters = [propertyFilter];
        }
      }
      props.updateEntry(Action.Edit, entry);
    } else if (isFormulaMenuSectionItem(sectionItem)) {
      const itemValue = sectionItem.value;
      let formulaItem: FormulaItem;

      if (itemValue.id) {
        formulaItem = {id: itemValue.id};
      }
      const entry: QueryFormulaEntry = {
        type: ResourceType.Formula,
        event: formulaItem,
      };
      props.updateEntry(Action.Edit, entry);
    } else if (isSavedMetricMenuSectionItem(sectionItem)) {
      const itemValue = sectionItem.value;
      let savedMetricItem: BehaviorShowClause;

      if (itemValue.id) {
        savedMetricItem = {
          id: itemValue.id,
          name: itemValue.name,
          type: TopLevelMetricType.Metric,
          behavior: {} as MetricBehavior,
          measurement: {} as MetricMeasurement,
        };
      } else {
        // This case should never happen.
        throw new Error(`Saved metric with no id.`);
      }
      const entry: QuerySavedMetricEntry = {
        type: MetricType.SavedMetric,
        event: savedMetricItem,
        isExpanded: false,
      };
      props.updateEntry(Action.Edit, entry);
    } else if (isFunnelMenuSectionItem(sectionItem) || isRetentionMenuSectionItem(sectionItem)) {
      const itemValue = sectionItem.value;
      const metricBehavior = getMetricEntryBehavior({
        ...itemValue.definition.behavior,
        type: itemValue.type,
        name: itemValue.name,
        id: itemValue.id,
      } as MetricBehavior);
      let entry: MetricEntry | null = null;
      if (prevEntry && isMetricEntry(prevEntry) && prevEntry.behavior.type === metricBehavior.type) {
        entry = {
          ...prevEntry,
          behavior: metricBehavior,
          hasUnsavedChanges: false,
        };
      } else {
        const newEntry =
          sectionItem.menuType === MenuType.Funnel
            ? getNewFunnelEntry({isExpanded: false})
            : getNewRetentionEntry({isExpanded: false, type: MetricType.Retention});
        entry = {
          ...newEntry,
          behavior: metricBehavior,
          hasUnsavedChanges: false,
        };
      }
      props.updateEntry(Action.Edit, entry, {
        requery: true,
      });
    } else if (isCohortMenuSectionItem(sectionItem)) {
      const itemValue = sectionItem.value;
      const event = filterToBookmarkablePropertyItem(
        pickBy(itemValue, (value) => value !== undefined) as PropertySectionItem,
      );
      const entry = {
        ...(dataGroupId ? {dataGroupId} : null),
        aggregationOperator: PRIMITIVE_MATH_TYPES.UNIQUE,
        aggregationOperatorPerUser: null,
        filters: [],
        filtersOperator: FiltersOperator.And,
        type: ResourceType.Cohort,
        event,
      } as QuerySuperEntry;
      props.updateEntry(Action.Edit, entry);
    }
  },
  handleRowCreate: (props, menuType, metadata) => {
    const firstClassMetricsEnabled = metadata?.featureFlags[ProjectFeatureFlag.FirstClassMetrics];
    if (firstClassMetricsEnabled) {
      props.updateEntry(
        Action.Edit,
        getNewMetricEntry({
          menuType,
          entries: props.getEntries(),
        }),
      );
      return;
    }

    if (menuType === MenuType.Funnel) {
      props.updateEntry(Action.Edit, getNewFunnelEntry({isExpanded: true}));
    } else if (menuType === MenuType.Retention) {
      props.updateEntry(Action.Edit, getNewRetentionEntry({isExpanded: true, type: MetricType.Retention}));
    } else if (menuType === MenuType.Formula) {
      props.updateEntry(Action.Edit, {
        type: ResourceType.Formula,
        event: getNewFormulaEntry(props.getEntries(), props.sectionProps.queryOrigin),
      });
    }

    props.setActivePart(EntryPart.SubValue, `open`);
  },
};
